'use client';;!function(){try{var e="undefined"!=typeof window?window:"undefined"!=typeof global?global:"undefined"!=typeof globalThis?globalThis:"undefined"!=typeof self?self:{};e._sentryModuleMetadata=e._sentryModuleMetadata||{},e._sentryModuleMetadata[(new e.Error).stack]=Object.assign({},e._sentryModuleMetadata[(new e.Error).stack],{"_sentryBundlerPluginAppKey:tillamook-com":true});}catch(e){}}();

import React, { useEffect } from 'react';

import cx from 'classnames';
import PropTypes from 'prop-types';

import { BLOCKS } from '@contentful/rich-text-types';

import Lockup from 'components/blocks/shared/Lockup';
import ButtonCTA from 'components/ui/ButtonCTA';
import Img from 'components/ui/Img';
import Parallax from 'components/ui/Parallax';
import RichText from 'components/ui/RichText';
import Text from 'components/ui/Text';
import Video from 'components/ui/Video';

import { useTheme } from 'hooks/useTheme';
import { blockRootProps } from 'utils';

import styles from './BlockFullWidthMedia.module.scss';

const BlockFullWidthMedia = ({
    __typename,
    anchorId,
    callout,
    className,
    flushBottom,
    flushTop,
    headline,
    richHeadline,
    headlineColor,
    headlineIcon,
    cta,
    headlinePortraitAlignment,
    headlineLandscapeAlignment,
    landscapeImage,
    loading,
    portraitImage,
    theme,
    videoTextureMp4,
    videoTextureWebm,
    videoPoster,
    videoTextureMp4Large,
    videoTextureWebmLarge,
    videoPosterLarge,
    svgLockup,
    svgPortraitAlignment,
    svgLandscapeAlignment,
    svgLockupSeoText,
    imageLargeVerticalAlignment,
    description,
    firstBlock,
    eyebrow,
}) => {
    headlineColor ??= 'cream';
    headlineIcon ??= 'morningStar';
    loading ??= 'lazy';
    theme ??= 'Default';

    const [globalTheme] = useTheme();

    const isMakersReserve = globalTheme === 'makers-reserve';
    const isSharpnessStory = theme === 'Sharpness Story';

    useEffect(() => {
        if (
            !portraitImage &&
            !landscapeImage &&
            !videoTextureMp4 &&
            !videoTextureWebm
        ) {
            dispatchWarning(
                'No background media provided. Image or video texture required for render.'
            );
        }

        if (
            (portraitImage && !landscapeImage) ||
            (landscapeImage && !portraitImage)
        ) {
            dispatchWarning(
                'Both portrait image and landscape image required for render.'
            );
        }

        if (
            (videoTextureMp4 && !videoTextureWebm) ||
            (videoTextureWebm && !videoTextureMp4)
        ) {
            dispatchWarning(
                'Both mp4 and webm video textures required for render.'
            );
        }
    }, [portraitImage, landscapeImage, videoTextureMp4, videoTextureWebm]);

    const dispatchWarning = warning => {
        if (warning) {
            console.warn(warning);
        }
    };

    const descriptionOverrides = {
        renderNode: {
            [BLOCKS.PARAGRAPH]: (node, children) => {
                return (
                    <Text.Theme
                        config={{
                            default: {
                                baseTheme: 'headingXSmall',
                                themes: {
                                    medium: 'headingSmall',
                                },
                            },
                        }}
                    >
                        {children}
                    </Text.Theme>
                );
            },
        },
    };

    const richTextOverrides = {
        // This headline will always come in as a paragraph from the CMS.
        // It is not possible to restrict input to H2.

        renderNode: {
            [BLOCKS.PARAGRAPH]: (node, children) => {
                return (
                    <Text.Theme
                        config={{
                            'makers-reserve': {
                                baseTheme: 'parkinsonXLarge',
                                themes: {
                                    large: 'parkinsonXXLarge',
                                },
                            },
                            default: {
                                baseTheme: 'displayMedium',
                                themes: {
                                    large: 'displayXXLarge',
                                },
                            },
                        }}
                        as={firstBlock ? 'h1' : 'h2'}
                    >
                        {children}
                    </Text.Theme>
                );
            },
        },
    };

    return (
        <>
            <div
                {...blockRootProps(anchorId, __typename)}
                className={cx(
                    styles.root,
                    styles[`theme--${globalTheme}`],
                    styles[
                        `headlinePortraitAlignment-${headlinePortraitAlignment}`
                    ],
                    styles[
                        `headlineLandscapeAlignment-${headlineLandscapeAlignment}`
                    ],
                    styles[`svgPortraitAlignment-${svgPortraitAlignment}`],
                    styles[`svgLandscapeAlignment-${svgLandscapeAlignment}`],
                    styles[
                        `imageLargeVerticalAlignment-${imageLargeVerticalAlignment}`
                    ],
                    className,
                    {
                        [styles[`theme--midpage`]]: theme === 'Midpage',
                        [styles[`theme--fullHeight`]]: theme === 'Full Height',
                        [styles[`theme--sharpnessStory`]]: isSharpnessStory,
                        [styles[`theme--headlineAlignLeft`]]:
                            theme === 'Headline Align Left',
                        [styles[`makersReserve`]]: isMakersReserve,
                    }
                )}
                style={{ color: `var(--colors-${headlineColor})` }}
            >
                {(headline || richHeadline) &&
                    !svgLockup &&
                    globalTheme !== 'makers-reserve' && (
                        <div className={styles.textContainer}>
                            <div className={styles.textContainerInner}>
                                <Lockup
                                    icon={headlineIcon}
                                    eyebrow={{ text: eyebrow }}
                                    headline={{
                                        as: firstBlock ? 'h1' : 'h2',
                                        text: richHeadline || headline,
                                    }}
                                    copy={{ text: description }}
                                    ctas={cta}
                                    theme="X-Large"
                                />
                            </div>
                        </div>
                    )}
                {svgLockup?.url && (
                    <div
                        className={styles.svgLockup}
                        style={{
                            '--svg-ratio': svgLockup?.height / svgLockup?.width,
                        }}
                    >
                        <Img
                            src={svgLockup.url}
                            alt=""
                            width={svgLockup?.width}
                            height={svgLockup?.height}
                        />
                        {firstBlock && svgLockupSeoText && (
                            <h1 className="sr-only">{svgLockupSeoText}</h1>
                        )}
                        {!firstBlock && svgLockupSeoText && (
                            <h2 className="sr-only">{svgLockupSeoText}</h2>
                        )}
                        {description && (
                            <div className={styles.description}>
                                <RichText
                                    richText={description}
                                    overrides={descriptionOverrides}
                                />
                            </div>
                        )}
                        {(cta?.link || cta?.assetDownloadLink) && (
                            <div className={styles.buttonWrapper}>
                                <ButtonCTA {...cta} />
                            </div>
                        )}
                    </div>
                )}
                {isMakersReserve && (
                    <div className={cx(styles.headline)}>
                        {richHeadline && (
                            <RichText
                                richText={richHeadline}
                                overrides={richTextOverrides}
                            />
                        )}
                    </div>
                )}
                <Parallax className={styles.aspect} disabled={firstBlock}>
                    {portraitImage?.url &&
                        landscapeImage?.url &&
                        !videoTextureMp4 &&
                        !videoTextureWebm && (
                            <Img
                                className={styles.image}
                                src={portraitImage.url}
                                width={portraitImage?.width}
                                height={portraitImage?.height}
                                fallbackImageWidth={768}
                                loading={loading}
                                alt={
                                    portraitImage.description ||
                                    portraitImage.title ||
                                    ''
                                }
                                priority={firstBlock}
                                customSources={[
                                    {
                                        breakpoint: 1920,
                                        src: landscapeImage.url,
                                        imageWidth: 1920 * 1.5,
                                    },
                                    {
                                        breakpoint: 1440,
                                        src: landscapeImage.url,
                                        imageWidth: 1440 * 1.5,
                                    },
                                    {
                                        breakpoint: 1024,
                                        src: landscapeImage.url,
                                        imageWidth: 1024 * 1.5,
                                    },
                                    {
                                        breakpoint: 768,
                                        src: landscapeImage.url,
                                        imageWidth: 768 * 1.5,
                                    },
                                    {
                                        src: portraitImage.url,
                                        imageWidth: 768,
                                    },
                                ]}
                            />
                        )}
                    {videoTextureMp4?.url && videoTextureWebm?.url && (
                        <Video
                            className={cx(styles.videoSmall, {
                                [styles['large-hide']]:
                                    videoTextureMp4Large?.url,
                            })}
                            poster={videoPoster?.url}
                            sources={[
                                videoTextureWebm.url,
                                videoTextureMp4.url,
                            ]}
                        />
                    )}
                    {videoTextureMp4Large?.url &&
                        videoTextureWebmLarge?.url && (
                            <Video
                                className={cx(styles.videoLarge, {
                                    [styles['small-hide']]:
                                        videoTextureMp4?.url,
                                })}
                                poster={videoPosterLarge?.url}
                                sources={[
                                    videoTextureWebmLarge.url,
                                    videoTextureMp4Large.url,
                                ]}
                            />
                        )}
                </Parallax>
            </div>
            {callout && (
                <div
                    className={cx(styles.calloutWrapper, {
                        [styles.flushBottom]: flushBottom,
                        [styles.flushTop]: flushTop,
                    })}
                >
                    {callout}
                </div>
            )}
        </>
    );
};

BlockFullWidthMedia.propTypes = {
    __typename: PropTypes.string,
    anchorId: PropTypes.string,
    callout: PropTypes.object,
    className: PropTypes.string,
    cta: PropTypes.object,
    description: PropTypes.object,
    eyebrow: PropTypes.any,
    firstBlock: PropTypes.bool,
    flushBottom: PropTypes.bool,
    flushTop: PropTypes.bool,
    headline: PropTypes.string,
    headlineColor: PropTypes.string,
    headlineIcon: PropTypes.string,
    headlineLandscapeAlignment: PropTypes.string,
    headlinePortraitAlignment: PropTypes.string,
    headlineSize: PropTypes.string,
    imageLargeVerticalAlignment: PropTypes.string,
    landscapeImage: PropTypes.shape({
        url: PropTypes.string.isRequired,
    }),
    loading: PropTypes.string,
    portraitImage: PropTypes.shape({
        title: PropTypes.string,
        description: PropTypes.string,
        url: PropTypes.string.isRequired,
    }),
    richHeadline: PropTypes.object,
    svgLandscapeAlignment: PropTypes.string,
    svgLockup: PropTypes.shape({
        url: PropTypes.string.isRequired,
        width: PropTypes.number.isRequired,
        height: PropTypes.number.isRequired,
    }),
    svgPortraitAlignment: PropTypes.string,
    svgLockupSeoText: PropTypes.string,
    theme: PropTypes.oneOf([
        'Default',
        'Midpage',
        'Full Height',
        'Headline Align Left',
        "Maker's Reserve",
        "Maker's Reserve w/ Border",
    ]),
    videoPoster: PropTypes.shape({
        url: PropTypes.string.isRequired,
    }),
    videoPosterLarge: PropTypes.shape({
        url: PropTypes.string.isRequired,
    }),
    videoTextureMp4: PropTypes.shape({
        url: PropTypes.string.isRequired,
    }),
    videoTextureMp4Large: PropTypes.shape({
        url: PropTypes.string.isRequired,
    }),
    videoTextureWebm: PropTypes.shape({
        url: PropTypes.string.isRequired,
    }),
    videoTextureWebmLarge: PropTypes.shape({
        url: PropTypes.string.isRequired,
    }),
};

export default BlockFullWidthMedia;
